import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import authService from '../../services/candidate/authService';
import { getJobs } from '../../services/public/jobService';
import { 
    toTitleCase, 
    cleanLocationString, 
    formatPostedTime, 
    formatContractType, 
    formatSalary, 
    formatExperience, 
    getJobIcon, 
    getJobIconBgColor, 
    getJobIconColor    
} from '../../utils/jobDataTransform';
import { formatRelativeTime } from '../../utils/dateFormatter';
import ApplyEmailModal from '../../components/jobs/ApplyEmailModal';
//import { saveJob, removeSavedJob, getSavedJobs } from '../../services/candidate/jobService';
import { useSavedJobs } from '../../hooks/useSavedJobs';

const JobListings = () => {
    const { styles, isPublic } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search);
    const urlQuery = searchParams.get('q') || '';
    const urlLocation = searchParams.get('location') || '';

    // Decode URL parameters
    const decodedQuery = urlQuery ? decodeURIComponent(urlQuery.replace(/\+/g, ' ')) : '';
    const decodedLocation = urlLocation ? decodeURIComponent(urlLocation.replace(/\+/g, ' ')) : '';

    // State
    const [searchData, setSearchData] = useState({
        query: decodedQuery || '',
        location: decodedLocation || ''
    });

    const [filters, setFilters] = useState({
        jobTypes: { fullTime: 0, partTime: 0, contract: 0, permanent: 0 },
        salaryRange: '',
        datePosted: '',
        sortBy: 'relevance'
    });

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalJobs: 0,
        totalAvailable: 0,
        resultsPerPage: 10,
        totalPages: 1,
    });

    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    //const [savedJobsIds, setSavedJobsIds] = useState([]);
    //const [savedJobs, setSavedJobs] = useState([]);
    
    const [mobileFilters, setMobileFilters] = useState({
        jobTypes: { fullTime: 0, partTime: 0, contract: 0, permanent: 0 },
        salaryRange: '',
        datePosted: ''
    });
    
    // Apply email modal state
    const [showApplyEmailModal, setShowApplyEmailModal] = useState(false);
    const [selectedJobForEmail, setSelectedJobForEmail] = useState(null);

    // Auth state
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    
    useEffect(() => {
        const isAuth = authService.isAuthenticated();
        setIsLoggedIn(!!isAuth);
    }, []);

    // Fetch saved jobs
    const { 
        savedJobsIds, 
        savedJobs,
        toggleSaveJob 
    } = useSavedJobs(isLoggedIn);

    /*useEffect(() => {
        const fetchSavedJobs = async () => {
            if (isLoggedIn) {
                try {
                    const response = await getSavedJobs();
                    if (response.success) {                        
                        setSavedJobs(response.data);
                        setSavedJobsIds(response.data.map(job => job.jobData.id));
                    }
                } catch (error) {
                    console.error('Error fetching saved jobs:', error);
                }
            }
        };
        
        fetchSavedJobs();
    }, [isLoggedIn]);*/


    // Salary options
    const salaryOptions = [
        { value: '10000', label: '10,000+ per month' },
        { value: '25000', label: '25,000+ per month' },
        { value: '50000', label: '50,000+ per month' },
        { value: '100000', label: '1,00,000+ per month' },
        { value: '150000', label: '1,50,000+ per month' }
    ];

    // Transform job to UI format
    const transformJobToUIFormat = (job) => ({
        id: job.id,
        title: toTitleCase(job.title) || 'Untitled Position',
        company: job.company || 'Company not specified',
        category: job.category,
        categoryLabel: job.categoryLabel || 'General',
        location: cleanLocationString(job.location?.display_name) || 'Location not specified',
        postedTime: formatRelativeTime(job.created),
        type: formatContractType(job.contractType),
        salary: formatSalary(job.salary),
        experience: formatExperience(job.experience),
        education: job?.education,
        workMode: job?.workMode,
        aiMatch: `AI Match: ${Math.floor(Math.random() * 20) + 80}%`,
        aiMatchColor: "#FFD166",
        description: job.description || 'No description available',
        skills: job.skills || ['Skills vary'],
        icon: getJobIcon(job.category || ''),
        iconBgColor: getJobIconBgColor(job.category || ''),
        iconColor: getJobIconColor(job.category || ''),
        redirectUrl: job.redirect_url,
        source: job.source || 'hunarpool',
        applyEmail: job.applyEmail,
        originalData: job
    });

    // Handle external job click
    const handleExternalJobClick = (job) => {        
        const jobData = {
            id: job.id,
            title: job.title,
            company: job.company,
            category: job.category,
            categoryLabel: job.categoryLabel,
            location: job.location,
            postedTime: job.postedTime,
            type: job.type,
            salary: job.salary,
            experience: job.experience,
            description: job.description,
            skills: job.skills,
            icon: job.icon,
            iconBgColor: job.iconBgColor,
            iconColor: job.iconColor,
            redirectUrl: job.redirectUrl,
            source: job.source,
            applyEmail: job.applyEmail,
            timestamp: Date.now()
        };
        
        localStorage.setItem(`job_${job.id}`, JSON.stringify(jobData));
        const navigateTo = isPublic ? `/jobs/${job.id}` : `/candidate/jobs/${job.id}`;
        navigate(navigateTo, { state: { fromJobs: true, jobSource: job.source } });
    };

    // Convert date posted to days
    const convertDatePostedToDays = (datePosted) => {
        switch(datePosted) {
            case '24hours': return '1';
            case '3days': return '3';
            case 'week': return '7';
            case 'month': return '30';
            default: return '';
        }
    };

    // Fetch jobs
    const fetchJobs = async (page = 1, isFilterChange = false) => {
        try {
            if (isInitialLoad || !isFilterChange) setLoading(true);
            if (isFilterChange) setIsFilterLoading(true);

            const payload = {
                query: searchData.query || '',
                location: searchData.location || '',
                salaryMin: filters.salaryRange || '',
                salaryMax: '',
                fullTime: filters.jobTypes.fullTime ? 1 : 0,
                partTime: filters.jobTypes.partTime ? 1 : 0,
                contract: filters.jobTypes.contract ? 1 : 0,
                permanent: filters.jobTypes.permanent ? 1 : 0,
                sortBy: filters.sortBy || 'relevance',
                maxDaysOld: convertDatePostedToDays(filters.datePosted) || '',
                page: page
            };

            Object.keys(payload).forEach(key => {
                if (payload[key] === '') delete payload[key];
            });

            const result = await getJobs(payload);
            
            if (result.success) {
                const transformedJobs = result.data.jobs.map(job => transformJobToUIFormat(job));
                setJobs(transformedJobs);
                setPagination(result.data.pagination);
                setError(null);
            } else {
                throw new Error(result.message || 'Failed to fetch jobs');
            }
        } catch (err) {
            console.error('Error fetching jobs:', err);
            setPagination({
                currentPage: 1,
                totalJobs: 2,
                totalAvailable: 0,
                resultsPerPage: 10,
                totalPages: 1,
            });
        } finally {
            setLoading(false);
            setIsFilterLoading(false);
            setIsInitialLoad(false);
        }
    };

    // Active filters
    const getActiveFilters = () => {
        const activeFilters = [];
        
        if (filters.jobTypes.fullTime) activeFilters.push({ label: "Full-time", type: 'jobType', key: 'fullTime' });
        if (filters.jobTypes.partTime) activeFilters.push({ label: "Part-time", type: 'jobType', key: 'partTime' });
        if (filters.jobTypes.contract) activeFilters.push({ label: "Contract", type: 'jobType', key: 'contract' });
        if (filters.jobTypes.permanent) activeFilters.push({ label: "Permanent", type: 'jobType', key: 'permanent' });
        
        if (filters.salaryRange) {
            const selectedSalary = salaryOptions.find(opt => opt.value === filters.salaryRange);
            if (selectedSalary) activeFilters.push({ label: selectedSalary.label, type: 'salary', key: 'salaryRange' });
        }
        
        if (filters.datePosted) {
            const dateLabels = {
                '24hours': 'Last 24 hours',
                '3days': 'Last 3 days',
                'week': 'Last week',
                'month': 'Last month'
            };
            activeFilters.push({ label: dateLabels[filters.datePosted], type: 'date', key: 'datePosted' });
        }

        return activeFilters;
    };

    // Initial fetch on component mount and when URL parameters change
    useEffect(() => {
        if (urlQuery || urlLocation) {
            setSearchData({ query: decodedQuery, location: decodedLocation });
        }        
        fetchJobs(1);
    }, [location.search]);

    // Refetch jobs when filters change, but not on initial load
    useEffect(() => {
        if (!isInitialLoad) {
            const timer = setTimeout(() => fetchJobs(1, true), 300);
            return () => clearTimeout(timer);
        }
    }, [filters]);

    // Handlers
    const handleSearch = () => {
        if (!searchData.query.trim()) return;

        const params = new URLSearchParams();
        params.append('q', searchData.query.trim());
        if (searchData.location.trim()) params.append('location', searchData.location.trim());
        
        // Redirect to the same page with updated query parameters
        const redirectUrl = isPublic ? `/jobs?${params.toString()}` : `/candidate/jobs?${params.toString()}`;        
        navigate(redirectUrl, { replace: true });
        fetchJobs(1);
    };

    const handleJobTypeChange = (key) => {
        setFilters(prev => ({
            ...prev,
            jobTypes: { ...prev.jobTypes, [key]: prev.jobTypes[key] ? 0 : 1 }
        }));
    };

    const handleSalaryChange = (value) => {
        setFilters(prev => ({ ...prev, salaryRange: prev.salaryRange === value ? '' : value }));
    };

    const handleDatePostedChange = (value) => {
        setFilters(prev => ({ ...prev, datePosted: prev.datePosted === value ? '' : value }));
    };

    const handleSortByChange = (value) => {
        setFilters(prev => ({ ...prev, sortBy: value }));
    };

    const handleRemoveFilter = (filter) => {
        if (filter.type === 'jobType') {
            setFilters(prev => ({ ...prev, jobTypes: { ...prev.jobTypes, [filter.key]: 0 } }));
        } else if (filter.type === 'salary') {
            setFilters(prev => ({ ...prev, salaryRange: '' }));
        } else if (filter.type === 'date') {
            setFilters(prev => ({ ...prev, datePosted: '' }));
        }
    };

    const clearAllFilters = () => {
        setFilters({
            jobTypes: { fullTime: 0, partTime: 0, contract: 0, permanent: 0 },
            salaryRange: '',
            datePosted: '',
            sortBy: 'relevance'
        });
    };

    /*const toggleSaveJob = async (job) => {
        if (!isLoggedIn) {
            navigate('/candidate/login');
            return;
        }

        // Optimistic update
        const isCurrentlySaved = savedJobsIds.includes(job.id);
        setSavedJobsIds(prev => 
            isCurrentlySaved 
                ? prev.filter(id => id !== job.id)
                : [...prev, job.id]
        );

        try {
            if (isCurrentlySaved) {
                // Get the saved job entry to remove
                const savedJobEntry = savedJobs.find(saved => saved.jobData.id === job.id);
                const savedJobId = savedJobEntry ? savedJobEntry._id : null;
                await removeSavedJob(savedJobId);
            } else {
                // Pass the entire job object to save
                await saveJob(job);
            }
        } catch (error) {
            // Revert on error
            setSavedJobsIds(prev => 
                isCurrentlySaved 
                    ? [...prev, job.id]
                    : prev.filter(id => id !== job.id)
            );
            console.error('Error toggling saved job:', error);
            // You might want to show a toast notification here
        }
    };*/

    const handleApplyNow = async (job) => {
        //console.log('Applying to job:', job);
        const { redirectUrl, source, jobId, applyEmail } = job;
        //console.log('Redirect URL:', redirectUrl);        

        if (isLoggedIn) {
            if (source === 'hunarpool' && applyEmail) {
                // show the popup of apply email                 
                setSelectedJobForEmail(job);
                setShowApplyEmailModal(true);
                return;
            }
            else if(source === 'hunarpool' && redirectUrl) {
                // Redirect to external URL for hunarpool jobs with redirect URL
                window.open(redirectUrl, '_blank', 'noopener,noreferrer');
                return;
            }
            else {
                // Redirect to adzuna redirect URL for external jobs
                window.open(redirectUrl, '_blank', 'noopener,noreferrer');
                return;
            }
        } else {
            navigate('/candidate/login', { 
                state: { from: location.pathname, jobRedirectUrl: redirectUrl, jobSource: source }
            });
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handlePageChange = (page) => {
        fetchJobs(page, true);
        window.scrollTo({ top: 0, behavior: 'auto' });
    };

    // Mobile filters
    const initializeMobileFilters = () => {
        setMobileFilters({
            jobTypes: { ...filters.jobTypes },
            salaryRange: filters.salaryRange,
            datePosted: filters.datePosted
        });
        setShowMobileFilters(true);
    };

    const handleMobileJobTypeChange = (key) => {
        setMobileFilters(prev => ({
            ...prev,
            jobTypes: { ...prev.jobTypes, [key]: prev.jobTypes[key] ? 0 : 1 }
        }));
    };

    const handleMobileSalaryChange = (value) => {
        setMobileFilters(prev => ({ ...prev, salaryRange: prev.salaryRange === value ? '' : value }));
    };

    const handleMobileDatePostedChange = (value) => {
        setMobileFilters(prev => ({ ...prev, datePosted: prev.datePosted === value ? '' : value }));
    };

    const applyMobileFilters = () => {
        setFilters(prev => ({
            ...prev,
            jobTypes: { ...mobileFilters.jobTypes },
            salaryRange: mobileFilters.salaryRange,
            datePosted: mobileFilters.datePosted
        }));
        setShowMobileFilters(false);
    };

    const resetMobileFilters = () => {
        setMobileFilters({
            jobTypes: { ...filters.jobTypes },
            salaryRange: filters.salaryRange,
            datePosted: filters.datePosted
        });
    };

    // Loading state
    /*if (isInitialLoad && loading) {
        return (
            <div className="container mx-auto px-4 md:px-6 pt-24 pb-8">
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0096C7] mx-auto mb-4"></div>
                        <p className='text-gray-400'>Hold on, Opportunities are loading...</p>
                    </div>
                </div>
            </div>
        );
    }*/

    const activeFilters = getActiveFilters();

    return (
        <div className={`${isPublic ? 'container mx-auto px-4 md:px-6 pt-24 pb-8' : ''}`}>
            {/* SEO Settings */}
            <title>{`Search Jobs in India – Explore Career Opportunities | ${process.env.REACT_APP_NAME}`}</title>
            <meta
                name="description"
                content="Browse the latest job openings across India on Hunar Pool. Find roles that match your skills and apply to opportunities that fit your career goals."
            />

            {/* Header */}
            <div className="mb-8">
                <h1 className={`${isPublic ? 'text-md md:text-4xl font-bold text-white mb-3' : 'text-2xl font-bold text-white'}`}>
                    Find Your Next Career Opportunity
                </h1>
                <p className="text-gray-400 text-sm md:text-[16px]">Discover jobs matched to your skills and preferences</p>
                
                {error && (
                    <div className="mt-4 p-4 bg-red-900/20 border border-red-700 rounded-xl">
                        <div className="flex items-center">
                            <i className="fas fa-exclamation-triangle text-red-500 mr-3"></i>
                            <p className="text-red-300">{error}</p>
                        </div>
                    </div>
                )}
                
                {/* Search Bar */}
                <div className="mt-6 w-full">
                    <div className={`search-shadow ${styles.cardBg} rounded-2xl p-2 w-full border ${styles.cardBorder}`}>
                        <div className="flex flex-col md:flex-row w-full">
                            <div className="flex-grow p-1 md:p-4">
                                <div className="flex items-center w-full">
                                    <div className="w-full">
                                        <div className="flex items-center mb-1">
                                            <i className={`fas fa-search ${styles.textMuted} mr-2 text-sm`}></i>
                                            <label className={`text-sm font-medium ${styles.textSecondary}`}>
                                                Job title, keywords, or company
                                            </label>
                                        </div>
                                        <input 
                                            type="text" 
                                            className={`w-full ${styles.inputText} text-sm md:text-lg p-2 bg-transparent focus:outline-none ${styles.inputPlaceholder}`}
                                            placeholder="e.g. 'Frontend Developer', 'Project Manager', 'Data Scientist'"
                                            value={searchData.query}
                                            onChange={(e) => setSearchData(prev => ({...prev, query: e.target.value}))}
                                            onKeyPress={handleKeyPress}
                                            disabled={loading && !isInitialLoad}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={`border-t md:border-t-0 md:border-l ${styles.border} p-1 md:p-4`}>
                                <div className="flex items-center h-full w-full">
                                    <div className="w-full">
                                        <div className="flex items-center mb-1">
                                            <i className={`fas fa-map-marker-alt ${styles.textMuted} mr-2 text-sm`}></i>
                                            <label className={`text-sm font-medium ${styles.textSecondary}`}>
                                                Location
                                            </label>
                                        </div>
                                        <input 
                                            type="text" 
                                            className={`w-full ${styles.inputText} text-sm md:text-lg p-2 bg-transparent focus:outline-none ${styles.inputPlaceholder}`}
                                            placeholder="City, country, or remote"
                                            value={searchData.location}
                                            onChange={(e) => setSearchData(prev => ({...prev, location: e.target.value}))}
                                            onKeyPress={handleKeyPress}
                                            disabled={loading && !isInitialLoad}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="p-0 md:p-4 mt-2 md:mt-0 flex items-center">
                                <button 
                                    className={`w-full md:w-auto ${styles.primaryGradient} ${styles.primaryHover} ${styles.primaryText} font-semibold px-6 md:px-8 py-2 md:py-4 rounded-xl flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    onClick={handleSearch}
                                    disabled={loading}
                                >
                                    {loading && !isInitialLoad ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                            <span className="text-lg">Searching...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-search mr-3"></i>
                                            <span className="text-lg">Search</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
                {/* Filters Sidebar - Desktop */}
                <div className="lg:w-1/4 hidden lg:block">
                    <div className={`sticky top-24 filter-sidebar ${styles.filterBg} rounded-2xl p-6 border ${styles.filterBorder} max-h-[calc(100vh-150px)] overflow-y-auto`}>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className={`text-xl font-bold ${styles.text}`}>Filters</h2>
                            <button 
                                className={`${styles.iconPrimary} hover:${isPublic ? 'text-[#00a8e0]' : 'text-primary-700'} text-sm font-medium`}
                                onClick={clearAllFilters}
                                disabled={loading}
                            >
                                Clear all
                            </button>
                        </div>

                        {/* Job Type Filter */}
                        <div className="mb-8">
                            <h3 className={`font-semibold ${styles.text} mb-4 flex items-center`}>
                                <i className={`fas fa-briefcase mr-2 ${styles.iconPrimary}`}></i>
                                Job Type
                            </h3>
                            <div className="space-y-3">
                                {[
                                    { key: 'fullTime', label: 'Full-time' },
                                    { key: 'partTime', label: 'Part-time' },
                                    { key: 'contract', label: 'Contract' },
                                    { key: 'permanent', label: 'Permanent' }
                                ].map((type) => (
                                    <label key={type.key} className="flex items-center cursor-pointer group">
                                        <div className="relative">
                                            <input 
                                                type="checkbox" 
                                                className="sr-only"
                                                checked={filters.jobTypes[type.key] === 1}
                                                onChange={() => handleJobTypeChange(type.key)}
                                                disabled={loading}
                                            />
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                                                filters.jobTypes[type.key] === 1 
                                                    ? `bg-[#0096C7] border-[#0096C7]` 
                                                    : `${isPublic ? 'border-gray-600 group-hover:border-gray-500' : 'border-gray-400 group-hover:border-gray-500'}`
                                            }`}>
                                                {filters.jobTypes[type.key] === 1 && (
                                                    <i className="fas fa-check text-white text-xs"></i>
                                                )}
                                            </div>
                                        </div>
                                        <span className={`ml-3 ${styles.textSecondary} group-hover:${styles.text} transition-colors`}>
                                            {type.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Salary Range */}
                        <div className="mb-8">
                            <h3 className={`font-semibold ${styles.text} mb-4 flex items-center`}>
                                <i className={`fas fa-money-bill-wave mr-2 ${styles.iconPrimary}`}></i>
                                Minimum Salary
                            </h3>
                            <div className="space-y-3">
                                {salaryOptions.map((option) => (
                                    <label key={option.value} className="flex items-center cursor-pointer group">
                                        <div className="relative">
                                            <input 
                                                type="radio" 
                                                name="salary-range"
                                                className="sr-only"
                                                checked={filters.salaryRange === option.value}
                                                onChange={() => handleSalaryChange(option.value)}
                                                disabled={loading}
                                            />
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                                                filters.salaryRange === option.value 
                                                    ? `border-[#0096C7]` 
                                                    : `${isPublic ? 'border-gray-600 group-hover:border-gray-500' : 'border-gray-400 group-hover:border-gray-500'}`
                                            }`}>
                                                <div className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                                                    filters.salaryRange === option.value 
                                                        ? `bg-[#0096C7]` 
                                                        : 'bg-transparent'
                                                }`}></div>
                                            </div>
                                        </div>
                                        <span className={`ml-3 ${styles.textSecondary} group-hover:${styles.text} transition-colors`}>
                                            {option.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Date Posted */}
                        <div className="mb-8">
                            <h3 className={`font-semibold ${styles.text} mb-4 flex items-center`}>
                                <i className={`fas fa-clock mr-2 ${styles.iconPrimary}`}></i>
                                Date Posted
                            </h3>
                            <div className="space-y-3">
                                {[
                                    { value: '24hours', label: 'Last 24 hours' },
                                    { value: '3days', label: 'Last 3 days' },
                                    { value: 'week', label: 'Last week' },
                                    { value: 'month', label: 'Last month' }
                                ].map(option => (
                                    <label key={option.value} className="flex items-center cursor-pointer group">
                                        <div className="relative">
                                            <input 
                                                type="radio" 
                                                name="date-posted"
                                                className="sr-only"
                                                checked={filters.datePosted === option.value}
                                                onChange={() => handleDatePostedChange(option.value)}
                                                disabled={loading}
                                            />
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                                                filters.datePosted === option.value 
                                                    ? `border-[#0096C7]` 
                                                    : `${isPublic ? 'border-gray-600 group-hover:border-gray-500' : 'border-gray-400 group-hover:border-gray-500'}`
                                            }`}>
                                                <div className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                                                    filters.datePosted === option.value 
                                                        ? `bg-[#0096C7]` 
                                                        : 'bg-transparent'
                                                }`}></div>
                                            </div>
                                        </div>
                                        <span className={`ml-3 ${styles.textSecondary} group-hover:${styles.text} transition-colors`}>
                                            {option.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Job Listings */}
                <div className="lg:w-3/4">
                    {/* Mobile Filter Button */}
                    <div className="lg:hidden flex justify-between items-center mb-6">
                        <div>
                            <h2 className={`text-xl font-bold text-white`}>
                                {searchData.query ? `${searchData.query} Jobs` : 'All Jobs'}
                            </h2>
                        </div>
                        <button 
                            className={`${isPublic ? 'bg-gray-800 border-gray-700 text-gray-300 hover:text-white hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:text-gray-900 hover:bg-gray-50'} px-4 py-2 rounded-lg flex items-center transition disabled:opacity-50`}
                            onClick={initializeMobileFilters}
                            disabled={loading}
                        >
                            <i className="fas fa-filter mr-2"></i>
                            Filters
                        </button>
                    </div>

                    {/* Active Filters */}
                    {activeFilters.length > 0 && (
                        <div className={`${styles.cardBg} rounded-2xl p-4 mb-6 border ${styles.cardBorder}`}>
                            <div className="flex flex-wrap items-center gap-3">
                                <span className={`${styles.textSecondary} font-medium`}>Active filters:</span>
                                {activeFilters.map(filter => (
                                    <div key={`${filter.type}-${filter.key}`} className={`flex items-center ${styles.chipBg} ${styles.chipText} px-3 py-1.5 rounded-lg text-sm`}>
                                        <span>{filter.label}</span>
                                        <button 
                                            className={`ml-2 ${styles.textMuted} hover:${styles.text}`}
                                            onClick={() => handleRemoveFilter(filter)}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                ))}
                                <button 
                                    className={`${styles.iconPrimary} hover:${isPublic ? 'text-[#00a8e0]' : 'text-primary-700'} text-sm font-medium ml-auto disabled:opacity-50`}
                                    onClick={clearAllFilters}
                                    disabled={loading}
                                >
                                    Clear all
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Sort and Count */}
                    <div className="flex justify-between items-center mb-6">
                        <div className="text-gray-400">
                            {isFilterLoading ? (
                                <span>Loading jobs...</span>
                            ) : (
                                <>
                                    <span className={`font-medium text-white`}>{pagination.totalAvailable.toLocaleString()}</span> jobs found
                                </>
                            )}
                        </div>
                        <div className="flex items-center space-x-4">
                            <select 
                                className={`${styles.inputBg} border ${styles.inputBorder} ${styles.inputText} rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0096C7] disabled:opacity-50`}
                                value={filters.sortBy}
                                onChange={(e) => handleSortByChange(e.target.value)}
                                disabled={loading}
                            >
                                <option value="relevance" className={styles.inputBg}>Relevance</option>
                                <option value="date" className={styles.inputBg}>Newest</option>
                                <option value="salary_desc" className={styles.inputBg}>Salary: High to Low</option>
                                <option value="salary_asc" className={styles.inputBg}>Salary: Low to High</option>
                            </select>
                        </div>
                    </div>

                    {/* Job Cards */}
                    {loading && jobs.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0096C7] mx-auto mb-4"></div>
                            <p className='text-gray-400'>Hold on, Opportunities are loading...</p>
                        </div>
                    ) : error && jobs.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
                            <p className='text-gray-400'>{error}</p>
                            <button 
                                className={`mt-4 ${styles.iconPrimary} hover:${isPublic ? 'text-[#00a8e0]' : 'text-primary-700'}`}
                                onClick={() => fetchJobs(1)}
                            >
                                Try Again
                            </button>
                        </div>
                    ) : jobs.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fas fa-search text-gray-500 text-4xl mb-4"></i>
                            <h3 className={`text-xl font-bold text-white mb-2`}>No Jobs Found</h3>
                            <p className="text-gray-400">Try adjusting your search or filters</p>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-4">
                                {jobs.map(job => (
                                    <div 
                                        key={job.id} 
                                        className={`${styles.cardBg} rounded-2xl border ${styles.cardBorder} ${styles.cardHover} transition-all duration-300 overflow-hidden`}
                                    >
                                        <div className="p-6">
                                            {/* Top Row */}
                                            <div className="flex items-start gap-4 mb-4">
                                                <div className={`w-14 h-14 rounded-xl ${job.iconBgColor} flex items-center justify-center flex-shrink-0`}>
                                                    <i className={`fas ${job.icon} text-2xl`} style={{ color: job.iconColor }}></i>
                                                </div>
                                                
                                                <div className="flex-1 min-w-0">
                                                    {job.source === 'hunarpool' ? (
                                                        <Link 
                                                            to={isPublic ? `/jobs/${job.id}` : `/candidate/jobs/${job.id}`}
                                                            state={{ jobData: job }}
                                                            className={`text-xl font-bold ${styles.text} hover:${styles.iconPrimary} transition-colors inline-block`}
                                                        >
                                                            {job.title}
                                                            <p className={`${styles.textMuted} font-medium text-base mt-1`}>{job.company}</p>
                                                        </Link>
                                                    ) : (
                                                        <div 
                                                            onClick={() => handleExternalJobClick(job)}
                                                            className="cursor-pointer"
                                                        >
                                                            <h3 className={`text-xl font-bold ${styles.text} hover:${styles.iconPrimary} transition-colors`}>
                                                                {job.title}                                                            
                                                            </h3>
                                                            <p className={`${styles.textMuted} font-medium text-base mt-1`}>{job.company}</p>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                    <button 
                                                        className={`p-2 ${savedJobsIds.includes(job.id) ? styles.iconPrimary : styles.textMuted} hover:${styles.iconPrimary}`}
                                                        onClick={() => toggleSaveJob(job)}
                                                    >
                                                        <i className={`${savedJobsIds.includes(job.id) ? 'fas' : 'far'} fa-heart text-xl`}></i>
                                                    </button>
                                                    
                                                    <button 
                                                        onClick={() => handleApplyNow(job)}
                                                        className="hidden md:block bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-medium px-6 py-2.5 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20 text-sm"
                                                    >
                                                        Apply Now
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Location and Posted Time */}
                                            <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                                                <span className={`flex items-center ${styles.textMuted}`}>
                                                    <i className={`fas fa-map-marker-alt mr-2 ${styles.iconPrimary} w-4`}></i>
                                                    {job.location}
                                                </span>
                                                <span className={`flex items-center ${styles.textMuted}`}>
                                                    <i className={`fas fa-clock mr-2 ${styles.iconPrimary} w-4`}></i>
                                                    {job.postedTime}
                                                </span>
                                            </div>

                                            {/* Job Details Chips */}
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                <span className={`px-3 py-1.5 ${styles.chipBg} ${styles.chipText} rounded-lg text-sm font-medium`}>
                                                    {job.categoryLabel}
                                                </span>
                                                <span className={`px-3 py-1.5 ${styles.chipBg} ${styles.chipText} rounded-lg text-sm font-medium`}>
                                                    {job.type}
                                                </span>
                                                <span className={`px-3 py-1.5 ${styles.chipBg} ${styles.chipSalary} rounded-lg text-sm font-medium`}>
                                                    {job.salary}
                                                </span>
                                                <span className={`px-3 py-1.5 ${styles.chipBg} ${styles.chipText} rounded-lg text-sm font-medium`}>
                                                    {job.experience}
                                                </span>
                                                {( job.workMode &&
                                                <span className={`px-3 py-1.5 ${styles.chipBg} ${styles.chipText} rounded-lg text-sm font-medium`}>
                                                    {job.workMode}
                                                </span>
                                                )}
                                            </div>

                                            {/* Description */}
                                            <p className={`${styles.textMuted} mb-4 line-clamp-2 leading-relaxed`}>
                                                {job.description}
                                            </p>

                                            {/* Skills */}
                                            <div className="flex flex-wrap gap-2 mb-6">
                                                {job.skills.map((skill, index) => (
                                                    <span 
                                                        key={index} 
                                                        className={`px-3 py-1.5 ${styles.chipBg} ${styles.chipText} rounded-lg text-sm`}
                                                    >
                                                        {skill}
                                                    </span>
                                                ))}
                                            </div>

                                            {/* Mobile Apply Button */}
                                            <div className="flex justify-end md:hidden">
                                                <button 
                                                    onClick={() => handleApplyNow(job)}
                                                    className="w-full bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-medium px-8 py-3 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20"
                                                >
                                                    Apply Now
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="mt-12 flex flex-col sm:flex-row justify-between items-center">
                                    <div className={`text-gray-400 mb-4 sm:mb-0`}>
                                        Showing <span className={`font-medium text-white`}>{((pagination.currentPage - 1) * pagination.resultsPerPage) + 1}-{Math.min(pagination.currentPage * pagination.resultsPerPage, pagination.totalJobs)}</span> of <span className={`font-medium text-white`}>{pagination.totalAvailable.toLocaleString()}</span> jobs
                                    </div>
                                    
                                    <div className="flex items-center space-x-2">
                                        <button 
                                            className={`w-10 h-10 flex items-center justify-center rounded-lg border ${styles.border} ${styles.textMuted} hover:${isPublic ? 'bg-gray-800' : 'bg-gray-50'} hover:${styles.text} disabled:opacity-50`}
                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                            disabled={pagination.currentPage === 1 || loading}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                        {Array.from({ length: Math.min(3, pagination.totalPages) }, (_, i) => i + 1).map(page => (
                                            <button 
                                                key={page}
                                                className={`w-10 h-10 flex items-center justify-center rounded-lg ${pagination.currentPage === page ? `${styles.primaryGradient} text-white` : `border ${styles.border} ${styles.textMuted} hover:${isPublic ? 'bg-gray-800' : 'bg-gray-50'} hover:${styles.text}`} disabled:opacity-50`}
                                                onClick={() => handlePageChange(page)}
                                                disabled={loading}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                        {pagination.totalPages > 3 && (
                                            <>
                                                <span className={`px-2 ${styles.textMuted}`}>...</span>
                                                <button 
                                                    className={`w-10 h-10 flex items-center justify-center rounded-lg border ${styles.border} ${styles.textMuted} hover:${isPublic ? 'bg-gray-800' : 'bg-gray-50'} hover:${styles.text} disabled:opacity-50`}
                                                    onClick={() => handlePageChange(pagination.totalPages)}
                                                    disabled={loading}
                                                >
                                                    {pagination.totalPages}
                                                </button>
                                            </>
                                        )}
                                        <button 
                                            className={`w-10 h-10 flex items-center justify-center rounded-lg border ${styles.border} ${styles.textMuted} hover:${isPublic ? 'bg-gray-800' : 'bg-gray-50'} hover:${styles.text} disabled:opacity-50`}
                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                            disabled={pagination.currentPage >= pagination.totalPages || loading}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Mobile Filters Overlay */}
            {showMobileFilters && (
                <div className="fixed inset-0 bg-black/50 z-50 mobile-filter-overlay">
                    <div className={`absolute right-0 top-0 h-full w-4/5 max-w-sm ${styles.overlayBg} overflow-y-auto`}>
                        <div className="p-6 pb-24">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className={`text-xl font-bold ${styles.text}`}>Filters</h2>
                                <button 
                                    className={styles.textMuted}
                                    onClick={() => setShowMobileFilters(false)}
                                >
                                    <i className="fas fa-times text-2xl"></i>
                                </button>
                            </div>
                            
                            <div className="space-y-6">
                                {/* Job Type */}
                                <div>
                                    <h3 className={`font-semibold ${styles.text} mb-4`}>Job Type</h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        {[
                                            { key: 'fullTime', label: 'Full-time' },
                                            { key: 'partTime', label: 'Part-time' },
                                            { key: 'contract', label: 'Contract' },
                                            { key: 'permanent', label: 'Permanent' }
                                        ].map((type) => (
                                            <button 
                                                key={type.key}
                                                className={`px-1 py-1 rounded-lg text-sm ${mobileFilters.jobTypes[type.key] === 1 ? (isPublic ? 'bg-white text-gray-800' : 'bg-gray-900 text-white') : (isPublic ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800')}`}
                                                onClick={() => handleMobileJobTypeChange(type.key)}
                                            >
                                                {type.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Salary Range */}
                                <div>
                                    <h3 className={`font-semibold ${styles.text} mb-4`}>Minimum Salary</h3>
                                    <div className="grid grid-cols-1 gap-3">
                                        {salaryOptions.map((option) => (
                                            <button 
                                                key={option.value}
                                                className={`px-4 py-1 text-sm rounded-lg text-left ${mobileFilters.salaryRange === option.value ? (isPublic ? 'bg-white text-gray-800' : 'bg-gray-900 text-white') : (isPublic ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800')}`}
                                                onClick={() => handleMobileSalaryChange(option.value)}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Date Posted */}
                                <div>
                                    <h3 className={`font-semibold ${styles.text} mb-4`}>Date Posted</h3>
                                    <div className="grid grid-cols-1 gap-3">
                                        {[
                                            { value: '24hours', label: 'Last 24 hours' },
                                            { value: '3days', label: 'Last 3 days' },
                                            { value: 'week', label: 'Last week' },
                                            { value: 'month', label: 'Last month' }
                                        ].map(option => (
                                            <button 
                                                key={option.value}
                                                className={`px-4 py-1 text-sm rounded-lg text-left ${mobileFilters.datePosted === option.value ? (isPublic ? 'bg-white text-gray-800' : 'bg-gray-900 text-white') : (isPublic ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800')}`}
                                                onClick={() => handleMobileDatePostedChange(option.value)}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Mobile Filter Actions */}
                        <div className={`fixed bottom-0 left-0 right-0 p-6 ${styles.overlayBg} border-t ${styles.overlayBorder}`}>
                            <div className="flex space-x-4">
                                <button 
                                    className={`flex-1 border ${styles.borderLight} ${styles.textSecondary} px-6 py-3 rounded-xl hover:${isPublic ? 'bg-gray-800' : 'bg-gray-50'}`}
                                    onClick={resetMobileFilters}
                                >
                                    Reset
                                </button>
                                <button 
                                    className={`flex-1 ${styles.primaryGradient} text-white px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-[#0096C7]/20`}
                                    onClick={applyMobileFilters}
                                >
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Apply Email Modal */}
            <ApplyEmailModal 
                isOpen={showApplyEmailModal}
                onClose={() => {
                    setShowApplyEmailModal(false);
                    setSelectedJobForEmail(null);
                }}
                job={selectedJobForEmail || {}}
            />
        </div>
    );
};

export default JobListings;